import { useEffect, useState, useRef, useCallback } from "react";
import { Container, TextField, Button, Grid, Dialog } from "@mui/material";
import { useAuth } from "../hooks/useAuth";
import { GetTokenIdentity } from "../utils/Token";
import { Icons } from "../utils/Icon";
import { CustomStyles } from "../utils/Styled";

import axios from "axios";
import moment from "moment";
import MaterialTable from "material-table";

import DateRangePicker from "../components/common/DateRangePicker";
import CreateTouchpointForm from "../components/forms/touchpoints/Create";
import CreateReminderForm from "../components/forms/touchpoints/CreateReminder";
import EditTouchpointForm from "../components/forms/touchpoints/Edit";
import DeleteTouchpointForm from "../components/forms/touchpoints/Delete";

export const OrganizationTouchpointsPage = ({ rowData }) => {
    const { token, logout } = useAuth();
    const identity = GetTokenIdentity(token);

    const classes = CustomStyles;

    const productInputRef = useRef(null);

    const [data, setData] = useState([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [sort, setSort] = useState({});
    const [filter, setFilter] = useState({
        start_date: null,
        end_date: null,
        product: "",
        org_id: rowData ? rowData.org_id : "",
    });

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const [open, setOpen] = useState({
        create: false,
        createReminder: false,
        edit: false,
    });
    const [selectedRow, setSelectedRow] = useState({});

    const formatDate = (date) => moment(date).format("YYYY-MM-DD");

    const handleDialogClose = () => {
        setIsDialogOpen(false);
    };

    const handleResetFilter = () => {
        setPage(0);
        setFilter({
            start_date: null,
            end_date: null,
            product: "",
            org_id: rowData ? rowData.org_id : "",
        });
        setStartDate(null);
        setEndDate(null);
    };

    const handleFilterChange = (event) => {
        setPage(0);
        setFilter({ ...filter, [event.target.name]: event.target.value });
    };

    const handleOpen = (type, row) => {
        setOpen({ ...open, [type]: true });
        setSelectedRow(row || null);
    };

    const handleClose = (type) => {
        setOpen({ ...open, [type]: false });
        setSelectedRow(null);
    };

    const fetchData = useCallback(async () => {
        try {
            let customFilter = { ...filter };
            const hasStartDate = moment(customFilter.start_date).isValid();
            const hasEndDate = moment(customFilter.end_date).isValid();

            customFilter.start_date = hasStartDate ? moment(customFilter.start_date).format("YYYY-MM-DD") : "";
            customFilter.end_date = hasEndDate ? moment(customFilter.end_date).format("YYYY-MM-DD") : "";

            // Default filter when date range is empty: show touchpoints up to today.
            if (!hasStartDate && !hasEndDate) {
                customFilter.end_date = moment().format("YYYY-MM-DD");
            }

            const { data } = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/touchpoints`, {
                params: {
                    page: page + 1,
                    perPage,
                    filter: JSON.stringify(customFilter),
                    sort,
                },
            });

            setData(data.data.touchpoints);
            setCount(data.data.total);
        } catch (error) {
            if (error.response && error.response.status === 401) {
                console.log("Unauthorized: ", error);
                logout("Your account has been logged out of a session.");
            } else {
                console.log(error);
            }
        }
    }, [page, perPage, filter, sort, logout]);

    useEffect(() => {
        fetchData();
    }, [fetchData, page, perPage, filter, sort]);

    useEffect(() => {
        setPage(0);
        setFilter((prevFilter) => ({ ...prevFilter, start_date: startDate, end_date: endDate }));
    }, [startDate, endDate]);

    const columns = [
        {
            title: "Date",
            field: "contact_date",
            cellStyle: { width: 140, maxWidth: 140 },
            headerStyle: { width: 140, maxWidth: 140 },
            render: (row) => moment(row.contact_date).format("YYYY-MM-DD"),
        },
        {
            title: "Remark",
            field: "remark",
            width: "60%",
        },
        {
            title: "Product",
            field: "product_description",
            width: "30%",
            render: (row) => row.product_description || "-",
        },
    ];

    const actions = [
        {
            icon: () => <Icons.Edit />,
            tooltip: "Edit Touchpoint",
            onClick: (event, rowData) => handleOpen("edit", rowData),
        },
        {
            icon: () => <Icons.Delete />,
            tooltip: "Delete Touchpoint",
            onClick: (event, rowData) => DeleteTouchpointForm(rowData, fetchData),
        },
    ];

    return (
        <div>
            <div className={classes.background} />
            <Container maxWidth="xl" sx={{ py: 2 }} style={{ padding: 10 }}>
                <MaterialTable
                    title="Customer Touchpoint List"
                    icons={Icons}
                    columns={columns}
                    data={data}
                    page={page}
                    totalCount={count}
                    style={{ padding: "0px", boxShadow: "unset" }}
                    options={{
                        pageSize: perPage,
                        pageSizeOptions: [10, 20, 30, 50],
                        search: false,
                        filtering: false,
                        sorting: false,
                        actionsColumnIndex: -1,
                        headerStyle: {
                            fontWeight: "bold",
                            backgroundColor: "#f1f1f1",
                            color: "#212122",
                        },
                        maxBodyHeight: "600px",
                        paginationType: "stepped",
                        padding: "dense",
                        draggable: false,
                    }}
                    localization={{
                        pagination: {
                            labelRowsPerPage: "",
                            labelDisplayedRows: "{from}-{to} of {count}",
                            labelRowsSelect: "rows",
                        },
                        toolbar: {
                            nRowsSelected: "{0} row(s) selected",
                        },
                        header: {
                            actions: "Actions",
                        },
                        body: {
                            emptyDataSourceMessage: "No data to display",
                            filterRow: {
                                filterTooltip: "Filter",
                            },
                        },
                    }}
                    actions={actions}
                    onChangePage={(nextPage) => setPage(nextPage)}
                    onChangeRowsPerPage={(nextPerPage) => {
                        setPerPage(nextPerPage);
                        setPage(0);
                    }}
                    components={{
                        Toolbar: () => (
                            <>
                                <div>
                                    <Grid container spacing={2} sx={{ px: 0.1, pb: 3 }}>
                                        <Grid item xs={12} sm={12} style={{ textAlign: "right", paddingTop: "8px" }}>
                                            {(
                                                <>
                                                    <Button
                                                        onClick={() => handleOpen("create", null)}
                                                        variant="outlined"
                                                        startIcon={<Icons.AddIcon fontWeight="bold" />}
                                                        color="primary"
                                                        sx={{
                                                            fontWeight: 800,
                                                            paddingTop: 0.5,
                                                            paddingBottom: 0.5,
                                                            marginRight: 1,
                                                        }}
                                                    >
                                                        Create Touchpoint
                                                    </Button>
                                                    <Button
                                                        onClick={() => handleOpen("createReminder", null)}
                                                        variant="outlined"
                                                        startIcon={<Icons.AddIcon fontWeight="bold" />}
                                                        color="primary"
                                                        sx={{
                                                            fontWeight: 800,
                                                            paddingTop: 0.5,
                                                            paddingBottom: 0.5,
                                                        }}
                                                    >
                                                        Create Reminder
                                                    </Button>
                                                </>
                                            )}
                                        </Grid>
                                    </Grid>
                                </div>
                                <form>
                                    <Grid container spacing={2} sx={{ paddingBottom: 1 }}>
                                        <Grid item xs={12} sm={2.5} md={1.8}>
                                            <TextField
                                                label="Date (Start)"
                                                fullWidth
                                                type="text"
                                                size="small"
                                                value={startDate ? formatDate(startDate) : ""}
                                                onClick={() => setIsDialogOpen(true)}
                                                InputProps={{ readOnly: true }}
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />
                                            <Dialog open={isDialogOpen} onClose={handleDialogClose}>
                                                <DateRangePicker onClose={handleDialogClose} startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate} />
                                            </Dialog>
                                        </Grid>
                                        <Grid item xs={12} sm={2.5} md={1.8}>
                                            <TextField
                                                label="Date (End)"
                                                fullWidth
                                                type="text"
                                                size="small"
                                                value={endDate ? formatDate(endDate) : ""}
                                                onClick={() => setIsDialogOpen(true)}
                                                InputProps={{ readOnly: true }}
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <TextField
                                                autoComplete="off"
                                                fullWidth
                                                label="Product"
                                                name="product"
                                                variant="outlined"
                                                autoFocus={productInputRef.current === document.activeElement}
                                                value={filter.product}
                                                onChange={handleFilterChange}
                                                inputRef={productInputRef}
                                                size="small"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={2} md={1}>
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                color="error"
                                                sx={{
                                                    color: "#ffffff",
                                                    marginTop: "2px",
                                                }}
                                                onClick={handleResetFilter}
                                            >
                                                Reset
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </form>
                            </>
                        ),
                    }}
                />
                <CreateTouchpointForm
                    open={open.create}
                    handleClose={() => handleClose("create")}
                    rowData={rowData}
                    fetchData={fetchData}
                />
                <CreateReminderForm
                    open={open.createReminder}
                    handleClose={() => handleClose("createReminder")}
                    rowData={rowData}
                    fetchData={fetchData}
                />
                <EditTouchpointForm
                    open={open.edit}
                    handleClose={() => handleClose("edit")}
                    rowData={selectedRow}
                    organizationData={rowData}
                    fetchData={fetchData}
                />
            </Container>
        </div>
    );
};